import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Search, Phone, Mail, Calendar, FileText, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [authToken, setAuthToken] = useState(localStorage.getItem('adminToken') || "");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authToken) {
      navigate("/admin");
      return;
    }
    fetchCustomers();
  }, [authToken, navigate]);

  useEffect(() => {
    filterCustomers();
  }, [searchTerm, customers]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      if (error.response?.status === 401) {
        navigate("/admin");
      } else {
        toast.error("Fehler beim Laden der Kundendaten");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    if (!searchTerm) {
      setFilteredCustomers(customers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = customers.filter(customer => {
      const name = `${customer.first_name} ${customer.last_name}`.toLowerCase();
      const phone = (customer.phone || "").toLowerCase();
      const email = (customer.email || "").toLowerCase();
      return name.includes(term) || phone.includes(term) || email.includes(term);
    });

    setFilteredCustomers(filtered);
  };

  const handleOpenNotes = (customer) => {
    setSelectedCustomer(customer);
    setNotes(customer.notes || "");
    setShowNotesDialog(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedCustomer) return;

    const identifier = selectedCustomer.phone || selectedCustomer.email;
    if (!identifier) {
      toast.error("Kein Telefon oder E-Mail vorhanden");
      return;
    }

    try {
      await axios.put(
        `${API}/customers/${encodeURIComponent(identifier)}/notes`,
        { notes },
        { headers: { Authorization: `Basic ${authToken}` } }
      );
      toast.success("Notizen gespeichert");
      setShowNotesDialog(false);
      fetchCustomers();
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/admin")}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück
              </Button>
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: '#2c2c2c' }}>
                  <Users className="w-8 h-8" style={{ color: '#c8232c' }} />
                  Kunden
                </h1>
                <p className="text-gray-600 mt-1">Kundenverwaltung & Notizen</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                Gesamt: <strong>{customers.length}</strong> Kunden
              </span>
            </div>
          </div>

          {/* Search */}
          <div className="mt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Suche nach Name, Telefon oder E-Mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-600">Lade Kundendaten...</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">
                {searchTerm ? "Keine Kunden gefunden" : "Noch keine Kunden vorhanden"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Kontakt</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Reservationen</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Letzte Reservierung</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Notizen</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredCustomers.map((customer, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">
                          {customer.first_name} {customer.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1 text-sm">
                          {customer.phone && (
                            <div className="flex items-center gap-2 text-gray-700">
                              <Phone className="w-4 h-4 text-gray-400" />
                              {customer.phone}
                            </div>
                          )}
                          {customer.email && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="w-4 h-4 text-gray-400" />
                              {customer.email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-800 font-bold">
                          {customer.total_reservations}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {customer.latest_date} {customer.latest_time}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Button
                          onClick={() => handleOpenNotes(customer)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          {customer.notes ? "Bearbeiten" : "Hinzufügen"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Notes Dialog */}
        <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Kundennotizen: {selectedCustomer?.first_name} {selectedCustomer?.last_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Notizen</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notizen zum Kunden (z.B. Allergien, Vorlieben, besondere Wünsche...)"
                  rows={8}
                  className="mt-2"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Diese Notizen sind nur intern sichtbar
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowNotesDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSaveNotes}
                  className="flex-1"
                  style={{ backgroundColor: '#c8232c' }}
                >
                  Speichern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Customers;
